import type { EndpointBuilder, MutationDefinition } from '@reduxjs/toolkit/query';
import type { BaseQueryFn, EndpointDefinitions } from '@reduxjs/toolkit/query';
import { createApi as createReduxApi } from '@reduxjs/toolkit/query/react';

const isMutationDefinition = <TBaseQuery extends BaseQueryFn>(
  endpoint: EndpointDefinitions[keyof EndpointDefinitions]
): endpoint is MutationDefinition<unknown, TBaseQuery, string, unknown> => {
  return 'invalidatesTags' in endpoint && 'query' in endpoint === false;
};

const invalidateTagsOnSuccess = (tags: unknown) => {
  return (_result: unknown, error: unknown) => (error ? [] : tags);
};

export const createApi = <
  TBaseQuery extends BaseQueryFn,
  TReducerPath extends string,
  TTagTypes extends string,
  TEndpoints extends EndpointDefinitions,
>(options: {
  reducerPath: TReducerPath;
  baseQuery: TBaseQuery;
  tagTypes?: readonly TTagTypes[];
  endpoints: (builder: EndpointBuilder<TBaseQuery, TTagTypes, TReducerPath>) => TEndpoints;
}) => {
  return createReduxApi({
    ...options,
    refetchOnFocus: false,
    refetchOnReconnect: false,
    endpoints: (builder) => {
      const originalEndpoints = options.endpoints(builder);
      const updatedEndpoints = { ...originalEndpoints } as TEndpoints;

      for (const key in originalEndpoints) {
        const endpoint = originalEndpoints[key];

        if (isMutationDefinition<TBaseQuery>(endpoint) && endpoint.invalidatesTags) {
          updatedEndpoints[key] = {
            ...endpoint,
            invalidatesTags: invalidateTagsOnSuccess(endpoint.invalidatesTags),
          } as TEndpoints[typeof key];
        }
      }

      return updatedEndpoints;
    },
  });
};
